import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config.js';
import { toApiError } from '../lib/api-error';

export interface SeedDataItem {
  id: string;
  seedWorkflowId: string;
  dataKey: string;
  name: string;
  description: string | null;
  displayLabel: string;
  attributesJson: Record<string, unknown> | null;
  lastSeedRunId: string | null;
  createdAt: string;
  updatedAt: string;
}

type SeedDataListResponse = {
  ok: boolean;
  data: SeedDataItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

export function useSeedDataQuery(params: {
  page?: number;
  pageSize?: number;
}) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 25;

  return useQuery<SeedDataListResponse>({
    queryKey: ['seed-data', { page, pageSize }],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', String(page));
      searchParams.set('pageSize', String(pageSize));

      const res = await fetch(
        `${API_URL}/org/seed-data?${searchParams.toString()}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to load seed data');
      return res.json();
    },
  });
}

function _useSeedDataDetail(params: { id: string | undefined }) {
  return useQuery<{ ok: boolean; data: SeedDataItem }>({
    queryKey: ['seed-data', 'detail', params.id],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/org/seed-data/${params.id}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to load seed data detail');
      return res.json();
    },
    enabled: !!params.id,
  });
}
